import { useState, useCallback } from 'react';
import { Question } from '../components/QuestionFlow';
import { BinarySearchAlgorithm } from '../utils/binarySearch';

export interface QuestionFlowState {
  currentQuestion: Question;
  isComplete: boolean;
  progress: number;
  resultFrequency: number | null;
  algorithm: BinarySearchAlgorithm;
}

export const useQuestionFlow = () => {
  const [algorithm] = useState(() => new BinarySearchAlgorithm());
  const [questionCount, setQuestionCount] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [resultFrequency, setResultFrequency] = useState<number | null>(null);
  
  const generateQuestion = useCallback((): Question => {
    return {
      id: questionCount.toString(),
      type: 'comparison',
      text: `Is your tinnitus frequency higher or lower than the sound you're hearing?`,
    };
  }, [questionCount]);

  const [currentQuestion, setCurrentQuestion] = useState<Question>(
    generateQuestion()
  );

  const processAnswer = useCallback((answer: string | number): number | null => {
    // Only handle string answers for comparison questions
    if (typeof answer !== 'string') return null;
    
    let nextFrequency: number | null = null;
    
    // Handle comparison answers
    if (answer === 'match') {
      nextFrequency = null;
      setIsComplete(true);
      setResultFrequency(algorithm.getState().currentFrequency);
    } else {
      // Process higher/lower response
      nextFrequency = algorithm.processResponse(answer as 'higher' | 'lower');
      
      if (nextFrequency) {
        setQuestionCount(prev => prev + 1);
        setCurrentQuestion(generateQuestion());
      } else if (algorithm.isComplete()) {
        setIsComplete(true);
        setResultFrequency(algorithm.getState().currentFrequency);
      }
    }
    
    if (algorithm.isComplete() && !isComplete) {
      setIsComplete(true);
      setResultFrequency(algorithm.getState().currentFrequency);
    }
    
    return nextFrequency;
  }, [algorithm, generateQuestion, isComplete]);

  const reset = useCallback(() => {
    algorithm.reset();
    setQuestionCount(1);
    setIsComplete(false);
    setResultFrequency(null);
    setCurrentQuestion(generateQuestion());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algorithm]);

  const getProgress = useCallback(() => {
    return algorithm.getProgress();
  }, [algorithm]);

  const getCurrentFrequency = useCallback(() => {
    return algorithm.getState().currentFrequency;
  }, [algorithm]);

  const restoreState = useCallback((savedState: {
    currentFrequency: number;
    minFreq: number;
    maxFreq: number;
    questionId: string;
  }) => {
    algorithm.setState({
      currentFrequency: savedState.currentFrequency,
      minFreq: savedState.minFreq,
      maxFreq: savedState.maxFreq,
    });
    setQuestionCount(parseInt(savedState.questionId));
    setCurrentQuestion(generateQuestion());
  }, [algorithm, generateQuestion]);

  return {
    currentQuestion,
    isComplete,
    progress: getProgress(),
    resultFrequency,
    processAnswer,
    reset,
    getCurrentFrequency,
    restoreState,
    algorithm,
  };
};